import { Injectable } from '@angular/core';
import * as Highcharts from 'highcharts';
import { DialogService } from 'primeng/dynamicdialog';
import { RawDataDisplayComponent } from './raw-data-display.component';

@Injectable()
export class ChartUtilitiesService {

	dialogService: DialogService;

	constructor ( dialogService: DialogService) {
		this.dialogService = dialogService;
    }

	// Adds a button after a "view data table" table has been created.
	// The location of the button is just before the table.
	addChartRemoveButton() {
		Highcharts.addEvent(this, 'afterViewData', function(table: HTMLElement) {
			const chart: any = this;
			const rmvButton = document.createElement('button');
			rmvButton.className = "generalFont";
		
			rmvButton.innerHTML = 'Remove table'
			table.insertBefore(rmvButton, table.childNodes[0]);
			rmvButton.addEventListener('click', function() {
				chart.dataTableDiv = null
				table.remove();
				rmvButton.remove();
			})
		})
	}

	// displayStationTimeSeries helper function:
	// Add a solid line to indicate trigger threshold on the graph.
	// This is cheesy, only for stage and only for type=upper - improve when precip alarms gets added by making more abstract
	// Needs to be an array of alarmTriggers rather than 1.
	addAlarmTriggerToSeriesArray( seriesArray, annotationArray, tsfile, minDate, maxDate, dataType, alarmTriggerType ) {

		console.log(minDate, maxDate);
		let alarmTrigger = null;
		if ( dataType === 'WaterLevelRiver' ) {
			const dataTypesInfo = tsfile.stationSummaries[0].dataTypes.filter(currentTS => currentTS.name === dataType)[0];
			if ( dataTypesInfo != null ) {
				const alarmTriggerIds = dataTypesInfo.alarmTriggerIds;
				// console.log(alarmTriggerIds);
				alarmTriggerIds.forEach(function(element) {
					const tmpAlarmTrigger = tsfile.alarmTriggers.filter(currentAlarm => currentAlarm.id === element)[0];
					if ( tmpAlarmTrigger.type === alarmTriggerType ) {
						alarmTrigger = tmpAlarmTrigger;

						if ( alarmTrigger != null ) {
							let alarmTitle = alarmTrigger.name;
							if ( alarmTitle === '' || alarmTitle == null ) {
								alarmTitle = alarmTrigger.description;
							}

							this.addLineToSeriesArray ( seriesArray, annotationArray, minDate, maxDate, alarmTrigger.limitValue,
								'red', 'Alarm', alarmTitle, 'red', 0, 'top');
						}
					}
				}, this);
			}
		}
	}

	// displayStationTimeSeries helper function:
	// Add a line at the yvalue from the minX to maxX value to indicate either an alarm trigger, bridge low chord, or any other threshold value
	// where a solid line and annotation is appropriate.
	// verticalAlign is the alignment of the annotation text: 'top', 'middle' or 'bottom'
	addLineToSeriesArray ( seriesArray, annotationArray,
						minX, maxX, yValue, lineColor, seriesName, annotationTitle, annotationColor, yadjustment, verticalAlign ) {

		// Verify the yValue is valid/not missing
		if ( yValue == -999 ) {
			console.log("Can't plot a missing value for " + annotationTitle );
			return;
		}

		console.log('in alarm triggers');
		const lineCoords = [];
		const leftP = [];
		leftP.push(minX);
		leftP.push(yValue);
		lineCoords.push(leftP);

		const rightP = [];
		rightP.push(maxX);
		rightP.push(yValue);
		lineCoords.push(rightP);
		// console.log(lineCoords);

		// if we don't have a series displayed, the chart doesn't scale to display the annotation.
		seriesArray.push ({
			data: lineCoords,
			name: seriesName,
			color: lineColor,
			marker: { enabled: false },
			showInLegend: false,
			gapSize: 0
		});

		annotationArray.push({
			labelOptions: {
				allowOverlap: true,
				verticalAlign: verticalAlign,  // 'bottom', // 'top',
				backgroundColor: 'rgba(0, 0, 0, 0)',
				shape: 'connector',
				borderWidth: 0,
				style: {
					color: annotationColor,
					borderWidth: 0
				},
				y: yadjustment
			},
			labels: [{
				point: {
					xAxis: 0,
					yAxis: 0,
					x: minX,
					y: yValue
				},
				text: annotationTitle,
				}
				]
			});
	}

	// Sort through all the time series in stationTSArray to find the earliest date of data.
	// stationTSArray holds the definitions for all the charts on one dashboard.
	// 		each element of stationTSArray is a chart, data contains the time series definitions (array), 
	//		and each data element contains multiple time series in ts (array).
	// Returns epoch time.
	getMinDate(stationTSArray) {

		if ( stationTSArray.length === 0 ) {
			return null;
		}

		let minDate = null;
		for ( let i = 0; i < stationTSArray.length; i++ ) {
			// console.log(stationTSArray[i]);
			for ( let j = 0; j < stationTSArray[i].data.length; j++ ) {
				for ( let k = 0; k < stationTSArray[i].data[j].ts.length; k++ ) {
					const ts = stationTSArray[i].data[j].ts[k];
					// console.log(ts);
					if ( ts !== null && ts !== undefined && ts.data.length > 0) {
						if ( minDate === null || ts.data[0].dt < minDate ) {
							// console.log('Setting min date');
							minDate = ts.data[0].dt;
						}
					}
				}
			}
		}

		// now convert min date so its type matches that of the data - a number, not an actual Date object
		// console.log(minDate);
		const minDateObject = new Date(Date.parse(minDate));
		return minDateObject.valueOf();
	}

	removeTrailingNulls ( stationTSArray) {
		for ( let i = 0; i < stationTSArray.length; i++ ) {
			for ( let j = 0; j < stationTSArray[i].data.length; j++ ) {
				for ( let k = 0; k < stationTSArray[i].data[j].ts.length; k++ ) {
					const ts = stationTSArray[i].data[j].ts[k];
					if ( ts != null && ts !== undefined && ts.data.length > 0) {
						// start at the end, find the last entry with non-null data
						const dataArray = ts.data;
						let index = dataArray.length - 1;
						while ( index >= 0 && dataArray[index].v == null ) {
							index--;
						}
						// if nulls were found, let's remove them
						if ( index < dataArray.length - 1 ) {
							dataArray.splice(index + 1);
						}
					}
				}
			}
		}

	}

	// Sort through all the time series in stationTSArray to find the last date of data.
	// stationTSArray holds the definitions for all the charts on one dashboard.
	// 		each element of stationTSArray is a chart, data contains the time series definitions (array), 
	//		and each data element contains multiple time series in ts (array).
	// Returns epoch time.
	getMaxDate(stationTSArray) {

		if ( stationTSArray.length === 0 ) {
			return null;
		}

		let maxDate = null;
		for ( let i = 0; i < stationTSArray.length; i++ ) {
			for ( let j = 0; j < stationTSArray[i].data.length; j++ ) {
				for ( let k = 0; k < stationTSArray[i].data[j].ts.length; k++ ) {
					const ts = stationTSArray[i].data[j].ts[k];
					if ( ts != null && ts !== undefined && ts.data.length > 0) {
						const maxDataDate = ts.data[ts.data.length - 1].dt;

						if ( maxDate === null || maxDataDate > maxDate ) {
							// console.log('setting maxDate');
							maxDate = maxDataDate;
						}
					}
				}
			}
		}

		// now convert max date so its type matches that of the data - a number, not an actual Date object
		const maxDateObject = new Date(Date.parse(maxDate));
		// console.log('maxDateObject', maxDateObject, maxDateObject.valueOf());
		return maxDateObject.valueOf();
	}

	// Given a groupScoreOrder, return the description.
	//
	convertGroupNovaScoreToDescription ( novaScores: any[], groupScoreOrder ) {  // may need to add scoreGroup
		const nsArray = novaScores.filter(function(stat) {
			return stat.groupScoreOrder === groupScoreOrder; });

		if ( nsArray.length > 0 ) {
			return nsArray[0].description;
		} else {
			return '';
		}
	}

	// Convert a novascore from novascore to groupScoreOrder.
	//
	convertNovaScore ( novaScores: any[], novascore ) {  // may need to add scoreGroup
		const nsArray = novaScores.filter(function(stat) {
									return stat.id === novascore; });

		if ( nsArray.length > 0 ) {
			return nsArray[0].groupScoreOrder;
		} else {
			return 1;
		}
	}

	// get novascores takes a station and uses the specs in stationTSArray
	// to retrieve the novascore for each data type
	// as well as set an overall novascore (max value of all novascores)
	// an array is returned
	// [{dataType: "Precip", novaScoreGroup": 3,
	// 		"novaScoreDescription": "Significant hydrologic activity"}, {...}]
	getNovaScores (stationTSArray, station: any, novaScoresDictionary) {
		const novaScores: any[] = [];
		const statisticsArray = station.statistics;

		// console.log(this.stationTSArray);
		for ( let i = 0; i < stationTSArray.length; i++) {
			// let stationConfig be the configuration for the current chart
			const chartData = stationTSArray[i].data;

			for ( let j = 0; j < chartData.length; j++) {
				const stationConfig = chartData[j];

				// In order to get the novascore, the statistics array must be filtered for data type, duration and statistic type.
				// However, sometimes, calculated data types have different data types in the statistics field such as 
				// precip-total.  Whereas every else, this referred to as precip-total, in the statistics, it is referred to as precip but 
				// with a statistic type of total.
				// console.log(stationConfig);
				let dataType = stationConfig.dataType.toUpperCase();
				if ( dataType.endsWith(stationConfig.statisticType.toUpperCase())) {
					// convert precip-total to precip, for example
					dataType = dataType.substring(0, dataType.length - ( stationConfig.statisticType.length + 1));
				}

				const novaScoreValue = statisticsArray.filter( stat =>
					stat.dataType.toUpperCase() === dataType &&
					((stat.duration != null) ? stat.duration.toUpperCase() === stationConfig.duration.toUpperCase() : true ) &&
					stat.statisticType.toUpperCase() === stationConfig.statisticType.toUpperCase() );
				// console.log(novaScoreValue);
				if ( novaScoreValue.length > 0 ) {
					const novaScoreValueGroup = this.convertNovaScore ( novaScoresDictionary, novaScoreValue[0].novaScore );
					const novaScoreDescription = this.convertGroupNovaScoreToDescription ( novaScoresDictionary, novaScoreValueGroup );

					const novaScoreObject = {};
					novaScoreObject['dataType'] = stationConfig.dataType;
					novaScoreObject['novaScoreGroup'] = novaScoreValueGroup;
					novaScoreObject['novaScoreDescription'] = novaScoreDescription;
					// console.log(novaScoreObject);
					novaScores.push( novaScoreObject );
				}
			}
		}
		return novaScores;
	}

	getWindSymbol ( date, windDirArray ) {
		const windDirArraySingle = windDirArray[0].data.filter( windDir => windDir.dt === date);
		if ( windDirArraySingle == null || windDirArraySingle.length === 0 ) {
			console.log( 'Unable to find wind direction for ' + date );
			return 'url(assets/images/arrows/arrow2-N.png)';
		}

		const windDir = windDirArraySingle[0].v;

		if ( windDir >= 22.5 && windDir <= 67.5 ) {
			return 'url(assets/images/arrows/arrow2-NE.png)';
		} else if ( windDir > 67.5 && windDir <= 112.5 ) {
			return 'url(assets/images/arrows/arrow2-E.png)';
		} else if ( windDir > 112.5 && windDir <= 157.5 ) {
			return 'url(assets/images/arrows/arrow2-SE.png)';
		} else if ( windDir > 157.5 && windDir <= 202.5 ) {
			return 'url(assets/images/arrows/arrow2-S.png)';
		} else if ( windDir > 202.5 && windDir <= 247.5 ) {
			return 'url(assets/images/arrows/arrow2-SW.png)';
		} else if ( windDir > 247.5 && windDir <= 292.5 ) {
			return 'url(assets/images/arrows/arrow2-W.png)';
		} else if ( windDir > 292.5 && windDir <= 337.5 ) {
			return 'url(assets/images/arrows/arrow2-NW.png)';
		} else {
			return 'url(assets/images/arrows/arrow2-N.png)';
		}
	}

	// displayStationTimeSeries helper function:
	// Take an individual description of the time series to display from this.stationTSArray
	// and convert it to a Highcharts format, an array of objects conttaining: x, y, marker spec, etc.
	// Stores the data in tsData, tsIndex is the index of the time series to process
	convertTSToHighchartsFormat( tsData, stationTSArray, tsIndex, markerEnabled ) {
		const ts = stationTSArray.ts[tsIndex];
		const dataType = stationTSArray.dataType;
		this.convertTSDataToHighchartsFormat(tsData, ts, dataType, markerEnabled, stationTSArray);
	}

	convertTSDataToHighchartsFormat(tsData, ts, dataType, markerEnabled, auxiliaryTSArray) {

		ts.data.forEach((element, index) => {
			const newObj = {};
			newObj['x'] = (new Date(Date.parse(element.dt))).valueOf();
			newObj['y'] = element.v;

			if ( dataType === 'WindSpeed') {
				newObj['marker'] = {
					'symbol': this.getWindSymbol(element.dt, auxiliaryTSArray['WindDir']),
					'enabled': true
				};
			} else {
				// enable the first and last data points marker
				const enabled = markerEnabled === true ? true : ( index === 0 || index === ts.data.length - 1) ? true : false;
				newObj['marker'] = {
					'enabled': enabled
				};
			}
			tsData.push(newObj);
		}, this);
		}


	bindMouseEvents(htmlelement) {
		console.log('The htmlelement:', htmlelement);
		/**  http://www.highcharts.com/demo/synchronized-charts
		 * In order to synchronize tooltips and crosshairs, override the
		 * built-in events with handlers defined on the parent element.
		 *
		 * see https://stackoverflow.com/questions/11966286/highcharts-tooltip-always-on-right-side-of-cursor
		 */
		htmlelement.bind('mousemove touchmove touchstart', function (e) {
			let chart,
				point,
				i,
				event;

			console.log('Number of charts: ', Highcharts.charts.length);
			for (i = 0; i < Highcharts.charts.length; i++) {
				chart = Highcharts.charts[i];
				event = chart.pointer.normalize(e.originalEvent); // Find coordinates within the chart
				point = chart.series[0].searchPoint(event, true); // Get the hovered point

				if (point) {
					point.highlight(e);
				}
			}
		});
		/**
		 * Override the reset function, we don't need to hide the tooltips and crosshairs.
		 */
		Highcharts.Pointer.prototype.reset = function () {
			return undefined;
		};

		/**
		 * Highlight a point by showing tooltip, setting hover state and draw crosshair
		 */
		Highcharts.Point.prototype.highlight = function (event) {
			this.onMouseOver(); // Show the hover marker
			this.series.chart.tooltip.refresh(this); // Show the tooltip
			this.series.chart.xAxis[0].drawCrosshair(event, this); // Show the crosshair
		};
		/* */
	}

	fixExportMenu () {
	Highcharts.Chart.prototype.addButton = function (options) {
		const chart = this;
		const renderer = chart.renderer;
		const btnOptions = Highcharts.merge(chart.options.navigation.buttonOptions, options);
		const onclick = btnOptions.onclick;
		const menuItems = btnOptions.menuItems;
		let symbol, button;
		const symbolSize = btnOptions.symbolSize || 12;
			if (!chart.btnCount) {
				chart.btnCount = 0;
			}
			// Keeps references to the button elements
			if (!chart.exportDivElements) {
				chart.exportDivElements = [];
				chart.exportSVGElements = [];
			}
			if (btnOptions.enabled === false) {
				return;
			}
			const attr = btnOptions.theme;
			const states = attr.states;
			const hover = states && states.hover;
			const select = states && states.select;
			let callback;
			if (!chart.styledMode) {
				attr.fill = Highcharts.pick(attr.fill, '#ffffff');
				attr.stroke = Highcharts.pick(attr.stroke, 'none');
			}
			delete attr.states;
			if (onclick) {
				callback = function(e) {
					if (e) {
						e.stopPropagation();
					}
					onclick.call(chart, e);
				};
			} else if (menuItems) {
				callback = function(e) {
				// consistent with onclick call (#3495)
				if (e) {
					e.stopPropagation();
				}
				chart.contextMenu(button.menuClassName, menuItems, button.translateX, button.translateY, button.width, button.height, button, e);
				button.setState(2);
				};
			}
			if (btnOptions.text && btnOptions.symbol) {
				attr.paddingLeft = Highcharts.pick(attr.paddingLeft, 25);
			} else if (!btnOptions.text) {
				Highcharts.extend(attr, {
				width: btnOptions.width,
				height: btnOptions.height,
				padding: 0
				});
			}
			if (!chart.styledMode) {
				attr['stroke-linecap'] = 'round';
				attr.fill = Highcharts.pick(attr.fill, '#ffffff');
				attr.stroke = Highcharts.pick(attr.stroke, 'none');
			}
			button = renderer
				.button(btnOptions.text, 0, 0, callback, attr, hover, select)
				.addClass(options.className)
				.attr({
				title: Highcharts.pick(chart.options.lang[btnOptions._titleKey || btnOptions.titleKey], '')
				});
			button.menuClassName = (options.menuClassName ||
				'highcharts-menu-' + chart.btnCount++);
			if (btnOptions.symbol) {
				symbol = renderer
				.symbol(btnOptions.symbol, btnOptions.symbolX - (symbolSize / 2), btnOptions.symbolY - (symbolSize / 2), symbolSize, symbolSize,
					// If symbol is an image, scale it (#7957)
					{
					width: symbolSize,
					height: symbolSize
					})
				.addClass('highcharts-button-symbol')
				.attr({
					zIndex: 1
				})
				.add(button);
				if (!chart.styledMode) {
				symbol.attr({
					stroke: btnOptions.symbolStroke,
					fill: btnOptions.symbolFill,
					'stroke-width': btnOptions.symbolStrokeWidth || 1
				});
				}
			}
			button
				.add(chart.exportingGroup)
				.align(Highcharts.extend(btnOptions, {
				width: button.width,
				x: Highcharts.pick(btnOptions.x, chart.buttonOffset) // #1654
				}), true, 'spacingBox');
			chart.buttonOffset += ((button.width + btnOptions.buttonSpacing) *
				(btnOptions.align === 'right' ? -1 : 1));
			chart.exportSVGElements.push(button, symbol);
			};

			Highcharts.Chart.prototype.contextMenu = function(className, items, x, y, width, height, button, event) {
			const chart = this;
			const navOptions = chart.options.navigation;
			const chartWidth = chart.chartWidth;
			const chartHeight = chart.chartHeight;
			const cacheName = 'cache-' + className;
			let menu = chart[cacheName];
			const menuPadding = Math.max(width, height); // for mouse leave detection
			let innerMenu, menuStyle;

			// create the menu only the first time
			if (!menu) {
				// create a HTML element above the SVG
				menu = Highcharts.createElement('div', {
					className: className
				}, {
					position: 'absolute',
					zIndex: 1000,
					padding: menuPadding + 'px',
					pointerEvents: 'auto'
				}, Highcharts.doc.body);
				chart[cacheName] = menu;
				chart.exportContextMenu = chart[cacheName];

				innerMenu = Highcharts.createElement('div', {
				className: 'highcharts-menu'
				}, null, menu);
				// Presentational CSS
				if (!chart.styledMode) {
				Highcharts.css(innerMenu, Highcharts.extend({
					MozBoxShadow: '3px 3px 10px #888',
					WebkitBoxShadow: '3px 3px 10px #888',
					boxShadow: '3px 3px 10px #888'
				}, navOptions.menuStyle));
				}
				// hide on mouse out
				menu.hideMenu = function() {
				Highcharts.css(menu, {
					display: 'none'
				});
				if (button) {
					button.setState(0);
				}
				chart.openMenu = false;
				Highcharts.css(chart.renderTo, {
					overflow: 'hidden'
				}); // #10361
				Highcharts.clearTimeout(menu.hideTimer);
				Highcharts.fireEvent(chart, 'exportMenuHidden');
				};
				// Hide the menu some time after mouse leave (#1357)
				chart.exportEvents.push(Highcharts.addEvent(menu, 'mouseleave', function() {
					menu.hideTimer = Highcharts.win.setTimeout(menu.hideMenu, 500);
				}), Highcharts.addEvent(menu, 'mouseenter', function() {
					Highcharts.clearTimeout(menu.hideTimer);
				}),
				// Hide it on clicking or touching outside the menu (#2258,
				// #2335, #2407)
				Highcharts.addEvent(Highcharts.doc, 'mouseup', function(e) {
					if (!chart.pointer.inClass(e.target, className)) {
					menu.hideMenu();
					}
				}), Highcharts.addEvent(menu, 'click', function() {
					if (chart.openMenu) {
					menu.hideMenu();
					}
				}));
				// create the items
				items.forEach(function(item) {
				if (typeof item === 'string') {
					item = chart.options.exporting
					.menuItemDefinitions[item];
				}
				if (Highcharts.isObject(item, true)) {
					let element;
					if (item.separator) {
					element = Highcharts.createElement('hr', null, null, innerMenu);
					} else {
					element = Highcharts.createElement('div', {
						className: 'highcharts-menu-item',
						onclick: function(e) {
						if (e) { // IE7
							e.stopPropagation();
						}
						menu.hideMenu();
						if (item.onclick) {
							item.onclick
							.apply(chart, arguments);
						}
						},
						innerHTML: (item.text ||
						chart.options.lang[item.textKey])
					}, null, innerMenu);
					if (!chart.styledMode) {
						element.onmouseover = function() {
						Highcharts.css(this, navOptions.menuItemHoverStyle);
						};
						element.onmouseout = function() {
						Highcharts.css(this, navOptions.menuItemStyle);
						};
						Highcharts.css(element, Highcharts.extend({
						cursor: 'pointer'
						}, navOptions.menuItemStyle));
					}
					}
					// Keep references to menu divs to be able to destroy them
					chart.exportDivElements.push(element);
				}
				});
				// Keep references to menu and innerMenu div to be able to destroy them
				chart.exportDivElements.push(innerMenu, menu);
				chart.exportMenuWidth = menu.offsetWidth;
				chart.exportMenuHeight = menu.offsetHeight;
			}
			menuStyle = {
				display: 'block'
			};

			if (event.clientX + chart.exportMenuWidth > Highcharts.doc.documentElement.clientWidth) {
				menuStyle.right = 0 + 'px';
			} else {
				menuStyle.left = event.clientX - menuPadding + 'px';
			}
			// if outside bottom, bottom align it
			// debugger
			/*
			if (event.clientY + chart.exportMenuHeight > Highcharts.doc.documentElement.clientHeight) {
				menuStyle.top = event.pageY - chart.exportMenuHeight + menuPadding + 'px';
				// menuStyle.bottom = 0 + 'px';
			} else {
				menuStyle.top = event.pageY - menuPadding + 'px';
			}
			*/
			// if outside bottom, bottom align it
			menuStyle.top = event.pageY - (event.clientY + chart.exportMenuHeight > Highcharts.doc.documentElement.clientHeight ?
					chart.exportMenuHeight - menuPadding : menuPadding) + 'px';

			Highcharts.css(menu, menuStyle);
			Highcharts.css(chart.renderTo, {
			overflow: ''
			}); // #10361
			chart.openMenu = true;
			Highcharts.fireEvent(chart, 'exportMenuShown');
		};
	}

	/**
     * Synchronize zooming through the setExtremes event handler.
     */

	syncExtremes(e) {
		console.log('In syncExtremes');
		if (e.trigger !== 'syncExtremes') { // Prevent feedback loop
			Highcharts.each(Highcharts.charts, function (chart) {
				// keep extremes for all charts the same
				if (chart.xAxis[0].setExtremes) { // It is null while updating
					chart.xAxis[0].setExtremes(e.min, e.max, undefined, false, { trigger: 'syncExtremes' });

					// redraw any alarms
					if ( chart.hasOwnProperty('renderedAlarms') && chart.renderedAlarms != null ) {
						chart.renderedAlarms.forEach ( alarm => {
							alarm.renderedImage.attr({
								x: chart.xAxis[0].toPixels(alarm.alarmDateTime) - (alarm.iconSize / 2)
							});
						});
					}
				}
			}, this);
		}
	}

	updateTickMarks = (e) => {
		console.log(e);
		// if (e.trigger !== 'syncExtremes') { // Prevent feedback loop
		console.log('In updateTickMarks');
			const diff = e.dataMax - e.dataMin;
			// convert to days
			let diffDays = diff / (7 * 24 * 3600 * 1000);
			console.log(diffDays);
			if ( diffDays < 2 )
				diffDays = 1;
			const xAxisFormat = this.formulateXAxisFormat(diffDays);
			const tickInterval = this.calculateTickInterval(diffDays);
			console.log(xAxisFormat);
			console.log(tickInterval);

			// Highcharts.charts.forEach(chart => {
				const chart = e.target.chart;
				chart.xAxis[0].update (
					{ 	labels: { format: xAxisFormat },
						tickInterval: tickInterval
					}
				);
			// });
		// }
	}

	formulateXAxisFormat ( numberOfDays ) {
		const xAxisFormat = ( numberOfDays > 365 ) ? '{value:%Y %b}' : 
					( numberOfDays === 365 ) ? '{value:%b}' :
					( numberOfDays < 1 ) ? '{value:%b %d, %I:%M %p}' :
					( numberOfDays == 1 ) ? '{value:%b %d, %I %p}' :
					'{value:%b %d}';

		return xAxisFormat;
	}

	// Set tick interval for 1 year (10 year POR), 
	// 1 month (3 month to 1 year POR),
	// 2 hours (1 day) and 
	// 1 day (all others) tick interval
	calculateTickInterval ( numberOfDays ) {
		const tickInterval = numberOfDays > 365 ? 
					12 * 30 * 24 * 3600 * 1000 :  // 1 year
				numberOfDays > 90 ? 
					90 * 24 * 3600 * 1000 : // 3 months
				numberOfDays > 30 ? 
					30 * 24 * 3600 * 1000 : // 1 month
				numberOfDays <= 1 ? 
					numberOfDays * 2 * 3600 * 1000 :
					24 * 3600 * 1000; // 1 day or less

		return tickInterval;
	}

	// This is the list of colors used when more than 1 time series is available for a given data type
	getColorList () {
		return ['blue', 'green', 'orange', 'deeppink', 'indigo', 'cyan', 'gray'];
	}

	// overwrite the Highcharts exporting dropdown menu
	// (1) add :
	// 		let exportMenu = this.getExportMenuDefinition();
	// (2) then include in the highcharts definition object:
	// 		exporting: exportMenu
	getExportMenuDefinition(dataDownloadAllowed: boolean) {
		// let the default show viewTable
		let self = this;
		console.log("dataDownloadAllowed: " + dataDownloadAllowed);
		let downloadOptions = (dataDownloadAllowed || dataDownloadAllowed == undefined) ? 
			['downloadPNG', 'downloadPDF', 'separator', 'downloadCSV', 'viewTable'] :
			['downloadPNG', 'downloadPDF'];
		return {
				menuItemDefinitions: {
					// Custom definition
					viewTable: {
						onclick: function (this) {
							// console.log(this.series);
							let ref = self.dialogService.open(RawDataDisplayComponent, {
								data: {
									chartSeries: this.series
								},
								closable: true,
								resizable: true,
								header: 'Chart data'
							});
						},
						text: 'View table'
					},
				},
				buttons: {
					contextButton: {
						menuItems: downloadOptions
					}
				}
		
		}

	}

	// calculate the adjustment necessary to add to max date so 
	// there is a little space on the right of the chart data
	getTodayAdjustment(ndays) {
		console.log(ndays);
		let adjustment = 0;
		const oneHour = 60*60*1000;
		const oneDay = 24*oneHour;
		if ( ndays <= .125 ) adjustment = oneHour/4.0; // 3 hours
		else if ( ndays <= .25 ) adjustment = oneHour/2.0; // 6 hours
		else if ( ndays <= .50) adjustment = oneHour; // 12 hours
		else if ( ndays <= 1.0) adjustment = 2*oneHour; // 1 day
		else if ( ndays <= 7.0) adjustment = oneDay; // 1 week
		else if ( ndays <= 30.0) adjustment = 2*oneDay; // 1 month
		else if ( ndays <= 90.0) adjustment = 6*oneDay; // 3 months
		else if ( ndays <= 180.0) adjustment = 10*oneDay; // 6 months
		else adjustment = 10*oneDay; // 1 year or more
		console.log(adjustment);
		return adjustment;
	}

}

